import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { UPDATE_ORDER_STATUS, GET_ORDER_BY_ID } from '../OrderQueries'
import { OrderStatus } from '@/types/order'
import { OrderStatusBadge } from '@/components/shared/OrderStatusBadge'
import { orderStatuses } from '@/utils/orderUtils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'

interface EditableOrderStatusProps {
  orderId: string
  currentStatus: OrderStatus
}

const ORDER_STATUS_OPTIONS: { value: OrderStatus; label: string }[] =
  Object.entries(orderStatuses).map(([key, label]) => ({
    value: key as OrderStatus,
    label: label,
  }))

export function EditableOrderStatus({
  orderId,
  currentStatus,
}: EditableOrderStatusProps) {
  const [isUpdating, setIsUpdating] = useState(false)

  const [updateOrderStatus] = useMutation(UPDATE_ORDER_STATUS, {
    refetchQueries: [
      {
        query: GET_ORDER_BY_ID,
        variables: { id: orderId },
      },
    ],
    onCompleted: () => {
      setIsUpdating(false)
      toast.success('Order status updated successfully')
    },
    onError: error => {
      setIsUpdating(false)
      toast.error(`Failed to update order status: ${error.message}`)
    },
  })

  const handleStatusChange = async (newStatus: OrderStatus) => {
    if (newStatus === currentStatus) return

    setIsUpdating(true)
    try {
      await updateOrderStatus({
        variables: {
          where: { id: orderId },
          order_status: newStatus,
        },
      })
    } catch (error) {
      console.error('Error updating order status:', error)
      setIsUpdating(false)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Select
        value={currentStatus}
        onValueChange={handleStatusChange}
        disabled={isUpdating}
      >
        <SelectTrigger className="w-auto border-none p-0 h-auto bg-transparent hover:bg-gray-50 focus:ring-0 focus:ring-offset-0">
          <OrderStatusBadge status={currentStatus} />
        </SelectTrigger>
        <SelectContent>
          {ORDER_STATUS_OPTIONS.map(option => (
            <SelectItem key={option.value} value={option.value}>
              <OrderStatusBadge status={option.value} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isUpdating && <div className="text-xs text-gray-500">Updating...</div>}
    </div>
  )
}
